import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { MARKET_CSS, ACCENT, GOLD } from "./constants";

// ── Utility ───────────────────────────────────────────────────────────────────
export function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── CSS Injector ──────────────────────────────────────────────────────────────
export function MarketStyleTag() {
  return <style>{MARKET_CSS}</style>;
}

// ── Background Effects ────────────────────────────────────────────────────────
export function FloatingOrbs() {
  const orbs = [
    { size: 380, x: "5%",  y: "15%", dur: 22, delay: 0,  color: "rgba(255,77,0,0.07)" },
    { size: 220, x: "78%", y: "8%",  dur: 18, delay: 4,  color: "rgba(245,200,66,0.06)" },
    { size: 300, x: "55%", y: "58%", dur: 26, delay: 8,  color: "rgba(255,77,0,0.05)" },
    { size: 160, x: "22%", y: "72%", dur: 20, delay: 2,  color: "rgba(245,200,66,0.05)" },
    { size: 200, x: "88%", y: "52%", dur: 24, delay: 12, color: "rgba(255,77,0,0.04)" },
  ];
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
      {orbs.map((o, i) => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            width: o.size, height: o.size,
            left: o.x, top: o.y,
            background: `radial-gradient(circle, ${o.color} 0%, transparent 70%)`,
            animation: `hm-float ${o.dur}s ease-in-out ${o.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

export function GrainOverlay() {
  return (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{
        zIndex: 1,
        opacity: 0.03,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundRepeat: "repeat",
        backgroundSize: "180px 180px",
        animation: "hm-grain 0.8s steps(1) infinite",
      }}
    />
  );
}

export function ScanlineBeam() {
  return (
    <div
      className="fixed left-0 right-0 pointer-events-none"
      style={{
        zIndex: 2,
        height: "2px",
        background: `linear-gradient(90deg, transparent, rgba(255,77,0,0.07), transparent)`,
        animation: "hm-scanline 8s linear infinite",
        top: 0,
      }}
    />
  );
}

// ── Count-up Hook ─────────────────────────────────────────────────────────────
export function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start   = prev.current;
    const diff    = target - start;
    const startTs = performance.now();
    function step(ts) {
      const elapsed  = ts - startTs;
      const progress = Math.min(elapsed / duration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(start + diff * eased));
      if (progress < 1) requestAnimationFrame(step);
      else prev.current = target;
    }
    requestAnimationFrame(step);
  }, [target, duration]);
  return val;
}

// ── Shimmer + Live Dot ────────────────────────────────────────────────────────
export function ShimmerSweep({ color = "rgba(255,255,255,0.15)", duration = "3s", delay = "0s" }) {
  return (
    <span
      className="absolute top-0 bottom-0 pointer-events-none overflow-hidden"
      style={{ inset: 0 }}
    >
      <span
        className="absolute top-0 bottom-0"
        style={{
          width: "45%",
          background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
          animation: `hm-gold-sweep ${duration} ease-in-out ${delay} infinite`,
        }}
      />
    </span>
  );
}

export function LiveDot({ color = ACCENT, size = 10 }) {
  return (
    <span className="relative inline-flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <span
        className="absolute rounded-full"
        style={{
          width: size, height: size,
          background: color,
          animation: "hm-pulse-ring 1.4s ease-out infinite",
          opacity: 0.6,
        }}
      />
      <span className="relative rounded-full" style={{ width: size * 0.6, height: size * 0.6, background: color }} />
    </span>
  );
}
